import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { useApp } from '../contexts/AppContext';
import api from '../api';
import { toast } from 'sonner';
import {
  MessageSquareText, Send, Plus, Trash2, Loader2, Sparkles, X, History, Bot, User,
} from 'lucide-react';

/** Rendu Markdown très léger (titres ## + puces -) */
const renderMarkdown = (text = '') => {
  const lines = text.split('\n');
  const out = [];
  let ulBuffer = [];
  const flushUl = (key) => {
    if (ulBuffer.length) {
      out.push(
        <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-1.5">
          {ulBuffer.map((li, i) => (
            <li key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(li) }} />
          ))}
        </ul>
      );
      ulBuffer = [];
    }
  };
  const inline = (s) =>
    String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-muted text-[12px]">$1</code>');
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^\s*-\s+/.test(line)) {
      ulBuffer.push(line.replace(/^\s*-\s+/, ''));
      return;
    }
    flushUl(i);
    if (/^##\s+/.test(line)) {
      out.push(
        <h4 key={i} className="font-black text-[13px] uppercase tracking-wider mt-2 mb-1">
          {line.replace(/^##\s+/, '')}
        </h4>
      );
    } else if (/^#\s+/.test(line)) {
      out.push(
        <h3 key={i} className="font-black text-sm mt-2 mb-1">
          {line.replace(/^#\s+/, '')}
        </h3>
      );
    } else if (line.trim() === '') {
      out.push(<div key={i} className="h-1.5" />);
    } else {
      out.push(
        <p key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  });
  flushUl('end');
  return out;
};

const SUGGESTIONS_BY_ROLE = {
  eleve: [
    'Rappelle-moi la règle C-S-D',
    'Donne-moi une mnémotechnique pour les priorités',
    'Quelles sont les limitations de vitesse hors agglomération ?',
    'Comment je fais pour réussir mon examen ETM ?',
  ],
  enseignant: [
    'Fais-moi un plan de cours 1h sur la circulation',
    'Grille d\'évaluation plateau — rappels clés',
    'Comment animer un oral CCS ?',
    'Différence C-S-D vs ancien protocole',
  ],
  admin: [
    'Comment rédiger une bonne question ETM ?',
    'Audit de contenu : quels pièges éviter ?',
    'Rappel des règles légales 2026',
    'Idées pour enrichir le Réseau Pro',
  ],
};

export const CoachChatWidget = () => {
  const { user } = useApp();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const role = user?.role || 'eleve';

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const r = await api.coachListConversations();
      setConversations(r.conversations || []);
    } catch (e) { /* silent */ }
  }, []);

  const openConversation = useCallback(async (id) => {
    setLoadingConv(true);
    try {
      const c = await api.coachGetConversation(id);
      setCurrentId(c.id);
      setMessages(c.messages || []);
      setShowHistory(false);
      scrollToBottom();
    } catch (e) {
      toast.error('Impossible de charger la conversation');
    } finally {
      setLoadingConv(false);
    }
  }, [scrollToBottom]);

  const startNew = useCallback(() => {
    setCurrentId(null);
    setMessages([]);
    setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const deleteConv = useCallback(async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await api.coachDeleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (currentId === id) startNew();
      toast.success('Conversation supprimée');
    } catch (e2) {
      toast.error('Suppression échouée');
    }
  }, [currentId, startNew]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input || '').trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);
    // Optimistic: ajoute le message user immédiatement
    const userEntry = { role: 'user', content: msg, ts: new Date().toISOString() };
    setMessages((prev) => [...prev, userEntry]);
    scrollToBottom();
    try {
      const r = await api.coachSend(msg, currentId);
      setCurrentId(r.conversation_id);
      setMessages((prev) => [...prev, r.assistant_message]);
      // Rafraîchir la liste pour mettre à jour l'ordre
      loadConversations();
      scrollToBottom();
    } catch (e) {
      toast.error('Le Coach IA est indisponible');
      setMessages((prev) => prev.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [input, sending, currentId, loadConversations, scrollToBottom]);

  useEffect(() => {
    if (open) {
      loadConversations();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, loadConversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Ne pas afficher si l'utilisateur n'est pas connecté
  if (!user) return null;

  // Masquer le FAB Coach dans les espaces où il chevauche le contenu en lecture
  // (Espace Pro Enseignant + Admin). Le FAB en position fixed bottom-right couvre
  // les boutons d'action et le texte des fiches pédagogiques sur mobile.
  // Les enseignants peuvent garder un accès dédié depuis leur dashboard si besoin.
  const pathname = location?.pathname || '';
  const HIDDEN_ON_PREFIXES = ['/enseignant', '/admin', '/reseau'];
  if (HIDDEN_ON_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const suggestions = SUGGESTIONS_BY_ROLE[role] || SUGGESTIONS_BY_ROLE.eleve;
  const isEmpty = messages.length === 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="coach-chat-fab"
          aria-label="Ouvrir le Coach IA"
          className={cn(
            'fixed z-[60] right-4 sm:right-6 shadow-lg transition-transform',
            'bottom-[92px] sm:bottom-24', // au-dessus du BottomNav mobile + "Made with Emergent"
            'rounded-full px-4 py-3 sm:px-5 sm:py-3.5',
            'bg-[hsl(var(--primary))] text-white font-black',
            'hover:scale-105 active:scale-95',
            'flex items-center gap-2 hide-in-oral no-print'
          )}
          style={{ boxShadow: '0 10px 30px rgba(207,32,49,0.35)' }}
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Coach&nbsp;IA</span>
          <span className="sm:hidden text-xs">Coach</span>
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0 h-full"
        data-testid="coach-chat-panel"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary)/0.12)] flex items-center justify-center">
                <Bot className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
              <div className="text-left">
                <SheetTitle className="text-base font-black">Coach&nbsp;C2R</SheetTitle>
                <SheetDescription className="text-[11px] text-muted-foreground">
                  IA pédagogique · {role === 'admin' ? 'Admin' : role === 'enseignant' ? 'Enseignant ECSR' : 'Élève'}
                </SheetDescription>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button" variant="ghost" size="sm"
                onClick={() => setShowHistory((v) => !v)}
                data-testid="coach-history-btn"
                aria-label="Historique"
                className="h-8 w-8 p-0 rounded-full"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                type="button" variant="ghost" size="sm"
                onClick={startNew}
                data-testid="coach-new-btn"
                aria-label="Nouvelle conversation"
                className="h-8 w-8 p-0 rounded-full"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Historique déroulant */}
        {showHistory && (
          <div className="border-b border-border bg-muted/40 max-h-56 overflow-y-auto" data-testid="coach-history-list">
            {conversations.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                Aucune conversation enregistrée.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {conversations.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => openConversation(c.id)}
                      className={cn(
                        'w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-accent/50 transition-colors',
                        currentId === c.id && 'bg-[hsl(var(--primary)/0.06)]'
                      )}
                      data-testid={`coach-history-item-${c.id}`}
                    >
                      <MessageSquareText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="flex-1 text-xs font-semibold truncate">{c.title}</span>
                      <span className="text-[10px] text-muted-foreground">{c.message_count || 0} msg</span>
                      <button
                        type="button"
                        onClick={(e) => deleteConv(c.id, e)}
                        aria-label="Supprimer"
                        className="p-1 rounded hover:text-[hsl(var(--primary))]"
                        data-testid={`coach-history-delete-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-background"
          data-testid="coach-messages"
        >
          {loadingConv ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : isEmpty ? (
            <div className="space-y-4">
              <div className="mc-card p-4 bg-[hsl(var(--primary)/0.04)] border-[hsl(var(--primary)/0.2)]">
                <div className="flex items-start gap-2.5">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                  <div className="text-xs leading-relaxed">
                    <strong className="font-black">Bonjour !</strong> Je suis le Coach C2R, ton assistant pédagogique pour le Code Moto 2026.
                    Pose-moi une question, je garderai le fil de la conversation.
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-1">
                  Suggestions
                </div>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => sendMessage(s)}
                    disabled={sending}
                    data-testid={`coach-suggestion-${i}`}
                    className="w-full text-left px-3 py-2 rounded-xl border-2 border-border bg-card hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.04)] text-xs font-semibold transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => {
              const mine = m.role === 'user';
              return (
                <div
                  key={i}
                  className={cn('flex gap-2', mine ? 'justify-end' : 'justify-start')}
                  data-testid={`coach-msg-${i}`}
                >
                  {!mine && (
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.12)] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
                      mine
                        ? 'bg-[hsl(var(--primary))] text-white rounded-br-sm'
                        : 'bg-card border border-border text-foreground rounded-bl-sm'
                    )}
                  >
                    {mine ? (
                      <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                    ) : (
                      <div className="space-y-1">{renderMarkdown(m.content)}</div>
                    )}
                  </div>
                  {mine && (
                    <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex gap-2 justify-start" data-testid="coach-typing">
              <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.12)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              </div>
              <div className="rounded-2xl rounded-bl-sm px-3.5 py-2.5 bg-card border border-border">
                <div className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="border-t border-border bg-card p-3 flex items-end gap-2"
          data-testid="coach-input-form"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pose ta question au Coach…"
            disabled={sending}
            data-testid="coach-input"
            className="flex-1 rounded-full h-10 text-sm"
            maxLength={1500}
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            data-testid="coach-send-btn"
            className="rounded-full h-10 w-10 p-0 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)]"
            aria-label="Envoyer"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>

        <div className="px-3 pb-3 text-[10px] text-center text-muted-foreground">
          Propulsé par GPT-4o-mini · réponses à valider avec ton moniteur
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CoachChatWidget;
